import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import WaterfallList from "./WaterfallList";
import type { WaterfallItem } from "./WaterfallList";
import EmptyState from "@/components/EmptyState";
import {
  fetchRecommendProductList,
  type RecommendProduct,
  type RecommendProductType,
} from "@/api/home";
import { buildDetailPath } from "@/utils/detailNav";
import { preloadImages, probeImageSupportBatch } from "@/utils/image";

/**
 * 「热门推荐」— 双列瀑布流（真实接口 + 触底加载更多）
 *
 * 接口：GET /home/index/productList
 *   params: city / longitude / latitude / type / page / limit
 *
 * 与顶部 Banner 的区别：Banner 用 /home/index/tourRecommend（线路推荐），
 * 这里用 productList（全类型商品 + 分页）。
 *
 * ★ 城市与坐标由父组件通过 props 传入（而非组件内部读 localStorage）：
 *   父组件写 localStorage 的 effect 晚于子组件挂载，
 *   子组件自行读取会拿到旧值，导致「换了城市但商品还是老城市的」。
 *
 * 分页策略：
 *   - 底部放一个「哨兵」元素，用 IntersectionObserver 监听进入视口
 *   - 触发后请求下一页并追加，已渲染的卡片不会重排（WaterfallList 内部去重）
 *   - 加载中 / 没有更多了 均有状态提示
 */

/** 每页条数 */
const PAGE_SIZE = 15;

/** 类型 → 角标文案（对齐 BLW 的角标语义） */
const TYPE_TAG: Record<RecommendProductType, string> = {
  tour: "热门线路",
  guide: "专业导游",
  hotel: "精选酒店",
  car: "省心租车",
};

// 类型 → 详情页路径 的映射已统一收敛到 @/utils/detailNav 的 DETAIL_ROUTE，
// 避免多处维护导致不一致（原先这里的映射缺少 ID 参数名，跳转后详情页取不到 ID）

/** 从图片 URL 中解析原始宽高（部分 CDN 图片带 ?width=&height=） */
function parseCoverSize(url: string): { width: number; height: number } {
  // 默认竖版比例（瀑布流观感更好），有明确宽高则用真实比例
  const qIndex = url.indexOf("?");
  if (qIndex !== -1) {
    const params = new URLSearchParams(url.slice(qIndex + 1));
    const w = Number(params.get("width"));
    const h = Number(params.get("height"));
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  }
  return { width: 300, height: 400 };
}

interface Props {
  /** 当前城市（由首页传入，如「北京市」） */
  city: string;
  /** 当前经纬度（由首页传入，后端按此查询） */
  latitude: number;
  /** 当前经度 */
  longitude: number;
}

export default function RecommendSection({ city, latitude, longitude }: Props) {
  const navigate = useNavigate();

  // 列表数据
  const [items, setItems] = useState<WaterfallItem[]>([]);
  // 分页状态
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  // 是否已完成首屏加载（用于区分首屏 loading 与加载更多的提示）
  const [firstLoaded, setFirstLoaded] = useState(false);

  // ★ 图片压缩能力探测结果到达后，用它触发一次重渲染，
  //   使已挂载的卡片从「原图 URL」切换到「压缩 URL」。
  //   值本身不参与渲染，仅作为重渲染的信号。
  const [, bumpRender] = useState(0);

  // ★ 已加载页码（用 ref 而非 state：供 IntersectionObserver 读取最新值，
  //   避免闭包捕获旧 page 导致重复请求同一页）
  const pageRef = useRef(0);
  // 防止并发重复请求
  const loadingRef = useRef(false);
  // 哨兵元素
  const sentinelRef = useRef<HTMLDivElement>(null);
  // 防止首屏 effect 重复执行（React 18 StrictMode 下会跑两次）
  const initedRef = useRef(false);

  /** 加载第 pageNum 页数据 */
  const loadPage = useCallback(
    async (pageNum: number) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);

      try {
        const res = await fetchRecommendProductList({
          city,
          longitude,
          latitude,
          type: "all",
          page: pageNum,
          limit: PAGE_SIZE,
        });

        const mapped: WaterfallItem[] = res.list.map((c: RecommendProduct) => {
          const size = parseCoverSize(c.cover_image);
          return {
            id: c.id,
            cover: c.cover_image,
            coverWidth: size.width,
            coverHeight: size.height,
            title: c.name,
            // 角标：按类型给文案
            tag: TYPE_TAG[c.type] || undefined,
            tagVariant: "dark" as const,
            // 直接用接口的 price_text（「¥1999.00/人起」「面议」「查看房型」）
            priceText: c.price_text || undefined,
            // 用 desc 作为副信息（如「成都·3天2晚·川西」）
            author: {
              nickname: c.desc || c.distance_text || "",
              avatar: "",
            },
            likeCount: undefined,
            liked: false,
            raw: c,
          };
        });

        // ★ 追加而非替换：WaterfallList 内部按 id 去重，已渲染卡片不受影响
        setItems((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...mapped.filter((m) => !seen.has(m.id))];
        });
        pageRef.current = res.page;
        setHasMore(res.has_more);

        // ★ 预加载本页图片：用户继续滚动时图片已在浏览器缓存中，感知延迟降低。
        //   首屏延后执行，避免与首屏图片争抢并发连接。
        if (typeof window !== "undefined") {
          const ratioMap = new Map(
            mapped.map((m) => [
              m.cover,
              m.coverWidth && m.coverHeight
                ? m.coverHeight / m.coverWidth
                : 1.33,
            ]),
          );
          const urls = mapped.map((m) => m.cover);
          const start = () =>
            preloadImages(urls, 170, (u) => ratioMap.get(u));
          if (pageNum === 1) {
            window.setTimeout(start, 800);
          } else {
            start();
          }
        }

        // ★ 图片压缩能力探测（仅首屏执行一次，探测结果按域名全局缓存）
        //
        //   背景：本项目图片源实测均不支持 URL 参数化缩放（加参会 400）。
        //   这里在后台静默探测一次：若后端后续开通了 OSS 图片处理等能力，
        //   探测会自动通过，之后所有列表图片自动启用压缩——前端无需改代码。
        //   探测请求极小（w_20），未开通时静默失败，不影响任何功能。
        if (pageNum === 1 && mapped.length > 0) {
          probeImageSupportBatch(mapped.map((m) => m.cover))
            .then((result) => {
              const supported = Object.entries(result)
                .filter(([, ok]) => ok)
                .map(([host]) => host);
              if (supported.length > 0) {
                console.log(
                  "%c[图片优化] 检测到后端支持压缩，已自动启用：\n" +
                    supported.map((h) => `  ✅ ${h}`).join("\n"),
                  "color:#12B0A0;font-weight:bold",
                );
                // ★ 探测结果是异步到达的，此时首屏图片已按原图 URL 渲染。
                //   这里触发一次重渲染，让已挂载的卡片也切换到压缩 URL。
                bumpRender((n) => n + 1);
              } else {
                console.log(
                  "%c[图片优化] 后端暂不支持图片压缩，使用原图直连。\n" +
                    "  如需提速，请后端开通 OSS 图片处理或提供缩略图字段。",
                  "color:#999",
                );
              }
            })
            .catch(() => {
              /* 探测失败静默处理，不影响业务 */
            });
        }
      } catch (e) {
        console.error("[RecommendSection] 加载推荐失败:", e);
        // 出错时不继续自动加载，避免无限重试
        setHasMore(false);
      } finally {
        setLoading(false);
        loadingRef.current = false;
        setFirstLoaded(true);
      }
    },
    // ★ 城市/坐标变化时重建 loadPage，配合下方 effect 重新拉取第 1 页
    [city, latitude, longitude],
  );

  // 加载第 1 页；城市或坐标变化时重新加载（清空旧数据，避免串数据）
  useEffect(() => {
    // 切换城市：重置分页与列表，让首页数据完全替换
    if (initedRef.current) {
      setItems([]);
      setHasMore(true);
      pageRef.current = 0;
      setFirstLoaded(false);
    }
    initedRef.current = true;
    loadPage(1);
  }, [loadPage]);

  // 触底检测：哨兵进入视口 → 加载下一页
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        // ★ 从 ref 读取最新页码，loadPage 内部有 loadingRef 兜底防并发
        loadPage(pageRef.current + 1);
      },
      {
        // 提前 300px 触发，滚动更顺滑
        rootMargin: "300px 0px",
        threshold: 0,
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  /** 卡片点击 → 按类型跳对应商品详情页 */
  const handleItemClick = useCallback(
    (item: WaterfallItem) => {
      const raw = item.raw as RecommendProduct | undefined;
      if (!raw) return;

      // ★ 按类型生成带 query 的路径（query 参数名与详情页约定一致，
      //   刷新页面后 ID 不丢失；原先只传 state，刷新即空白）
      const path = buildDetailPath(raw.type, raw.id);
      if (!path) return;

      navigate(path, {
        // 各详情页约定的 ID 字段名不同，这里统一都带上，页面自行取用
        state: {
          routeId: raw.id,
          guideId: raw.id,
          hotelId: raw.id,
          carId: raw.id,
          productId: raw.id,
          id: raw.id,
          product: raw,
        },
      });
    },
    [navigate],
  );

  return (
    <div className="mt-6">
      <div className="flex items-center mb-3 px-5">
        {/* 标题：加粗深色（对齐 UI 设计稿） */}
        <div className="flex-shrink-0">
          <span className="section-title-hot text-[19px] leading-none">
            热门推荐
          </span>
        </div>

        {/* 右侧填充：斜向点阵 + 掠光，向右侧淡出 */}
        <div className="relative flex-1 h-6 ml-3 overflow-hidden">
          <div className="hot-dots absolute inset-0" />
          <div className="hot-glow absolute top-0 h-full w-16" />
        </div>

        {/* 「查看全部」入口（暂时注释，后期可能启用）
        <span
          className="text-sm text-gray-400 cursor-pointer"
          onClick={() => navigate("/discover")}
        >
          查看全部 →
        </span>
        */}
      </div>

      {/* 首屏加载中 */}
      {!firstLoaded && loading && (
        <div className="py-10 flex items-center justify-center">
          <span className="text-sm text-gray-400">加载中...</span>
        </div>
      )}

      {/* 首屏无数据 */}
      {firstLoaded && items.length === 0 && !loading && (
        <EmptyState text="暂无推荐内容" size={72} className="py-10" />
      )}

      {items.length > 0 && (
        <WaterfallList
          items={items}
          column={2}
          onItemClick={handleItemClick}
        />
      )}

      {/* ── 触底哨兵 + 状态提示 ── */}
      <div ref={sentinelRef} className="py-5 flex items-center justify-center">
        {loading && firstLoaded && (
          <span className="text-sm text-gray-400 flex items-center gap-2">
            <i
              className="ri-loader-4-line animate-spin"
              style={{ fontSize: 15 }}
            />
            加载更多...
          </span>
        )}
        {!loading && !hasMore && items.length > 0 && (
          <span className="text-xs text-gray-300">— 没有更多了 —</span>
        )}
      </div>
    </div>
  );
}
